// Lightweight autocomplete for the `boo` / `pm` script API used in pre-request and
// test scripts. Replaces the Monaco TypeScript language service (`javascriptDefaults
// .addExtraLib`), which dragged the entire ~6.5 MB TS worker into the bundle just to
// power member completion on a small, fixed API surface.
//
// This walks a hand-written object graph (mirrors the old `.d.ts`) and offers member
// completions after `boo.` / `pm.` chains — no type inference, no worker, ~0 KB.
//
// The `snippet` strings below use Monaco's `${1:placeholder}` / `$0` tab-stop syntax in
// plain strings (not template literals) — that's intentional, so disable the lint that
// guards against accidental template-literal interpolation.
/* eslint-disable no-template-curly-in-string */

// Each type is a map of member name -> { kind, detail, doc, snippet?, next? }.
//  - `next`  : type name to navigate into when this member is followed by `.`
//              (for methods it is effectively the return type).
//  - `snippet`: when set, inserted as a snippet (so `()` and a cursor stop appear).
const M = 'method';
const P = 'property';

const TYPES = {
  Boo: {
    test: { kind: M, detail: '(name: string, fn: () => void): void', doc: 'Register a named test. Use `boo.expect` or throw to fail.', snippet: 'test("${1:name}", () => {\n\t$0\n})' },
    expect: { kind: M, detail: '(value: any): BooExpectChain', doc: 'Start a Chai-style assertion chain.', snippet: 'expect(${1:value})', next: 'BooExpectChain' },
    environment: { kind: P, detail: 'BooEnvironment', doc: 'Read and write environment variables. Changes persist to the active environment.', next: 'BooEnvironment' },
    variables: { kind: P, detail: 'BooVariables', doc: 'Request-scoped local variables. Set here to use as {{key}} in URL, headers, or body.', next: 'BooVariables' },
    request: { kind: P, detail: 'BooRequest', doc: 'Details about the outgoing request (read-only).', next: 'BooRequest' },
    response: { kind: P, detail: 'BooResponse', doc: 'The response. Only available in test scripts — null in pre-request scripts.', next: 'BooResponse' },
  },
  BooEnvironment: {
    get: { kind: M, detail: '(key: string): string | null', doc: 'Get an environment variable by key.', snippet: 'get("${1:key}")' },
    set: { kind: M, detail: '(key: string, value: any): void', doc: 'Set an environment variable.', snippet: 'set("${1:key}", ${2:value})' },
    unset: { kind: M, detail: '(key: string): void', doc: 'Delete an environment variable.', snippet: 'unset("${1:key}")' },
    has: { kind: M, detail: '(key: string): boolean', doc: 'Check whether an environment variable exists.', snippet: 'has("${1:key}")' },
  },
  BooVariables: {
    get: { kind: M, detail: '(key: string): string | null', doc: 'Get a request-scoped local variable.', snippet: 'get("${1:key}")' },
    set: { kind: M, detail: '(key: string, value: any): void', doc: 'Set a request-scoped local variable (usable as {{key}} in URL, headers, body).', snippet: 'set("${1:key}", ${2:value})' },
    unset: { kind: M, detail: '(key: string): void', doc: 'Delete a local variable.', snippet: 'unset("${1:key}")' },
    has: { kind: M, detail: '(key: string): boolean', doc: 'Check whether a local variable exists.', snippet: 'has("${1:key}")' },
  },
  BooRequest: {
    url: { kind: P, detail: 'string', doc: 'The full request URL.' },
    method: { kind: P, detail: 'string', doc: 'HTTP method in uppercase (e.g. "GET", "POST").' },
    headers: { kind: P, detail: 'BooHeaders', doc: 'Request headers.', next: 'BooHeaders' },
  },
  BooResponse: {
    status: { kind: P, detail: 'number', doc: 'HTTP status code (e.g. 200).' },
    code: { kind: P, detail: 'number', doc: 'HTTP status code alias.' },
    responseTime: { kind: P, detail: 'number', doc: 'Response time in milliseconds.' },
    json: { kind: M, detail: '(): any', doc: 'Parse and return the response body as JSON.', snippet: 'json()' },
    text: { kind: M, detail: '(): string', doc: 'Return the raw response body as a string.', snippet: 'text()' },
    headers: { kind: P, detail: 'BooHeaders', doc: 'Response headers.', next: 'BooHeaders' },
    to: { kind: P, detail: 'BooResponseTo', doc: 'Fluent response assertions.', next: 'BooResponseTo' },
  },
  BooResponseTo: {
    have: { kind: P, detail: 'BooResponseToHave', next: 'BooResponseToHave' },
    be: { kind: P, detail: 'BooResponseToBe', next: 'BooResponseToBe' },
  },
  BooResponseToHave: {
    status: { kind: M, detail: '(code: number): void', doc: 'Assert the response status equals `code`.', snippet: 'status(${1:code})' },
    header: { kind: M, detail: '(name: string): void', doc: 'Assert the response includes header `name`.', snippet: 'header("${1:name}")' },
  },
  BooResponseToBe: {
    ok: { kind: M, detail: '(): void', doc: 'Assert the response status is 2xx.', snippet: 'ok()' },
  },
  BooHeaders: {
    get: { kind: M, detail: '(name: string): string | null', doc: 'Get a header value by name (case-insensitive).', snippet: 'get("${1:name}")' },
    toObject: { kind: M, detail: '(): Record<string, string>', doc: 'Return all headers as a plain object.', snippet: 'toObject()' },
  },
  BooExpectChain: {
    equal: { kind: M, detail: '(expected: any): BooExpectChain', doc: 'Assert the value strictly equals `expected`.', snippet: 'equal(${1:expected})', next: 'BooExpectChain' },
    eql: { kind: M, detail: '(expected: any): BooExpectChain', doc: 'Assert deep equality (JSON comparison).', snippet: 'eql(${1:expected})', next: 'BooExpectChain' },
    include: { kind: M, detail: '(item: any): BooExpectChain', doc: 'Assert the string or array includes `item`.', snippet: 'include(${1:item})', next: 'BooExpectChain' },
    a: { kind: M, detail: '(type: string): BooExpectChain', doc: 'Assert the value is of type `type` (e.g. "string", "number", "array").', snippet: 'a("${1:type}")', next: 'BooExpectChain' },
    below: { kind: M, detail: '(n: number): BooExpectChain', doc: 'Assert a numeric value is below `n`.', snippet: 'below(${1:n})', next: 'BooExpectChain' },
    above: { kind: M, detail: '(n: number): BooExpectChain', doc: 'Assert a numeric value is above `n`.', snippet: 'above(${1:n})', next: 'BooExpectChain' },
    lengthOf: { kind: M, detail: '(n: number): BooExpectChain', doc: 'Assert the value has `.length === n`.', snippet: 'lengthOf(${1:n})', next: 'BooExpectChain' },
    property: { kind: M, detail: '(key: string): BooExpectChain', doc: 'Assert the object has property `key`.', snippet: 'property("${1:key}")', next: 'BooExpectChain' },
    status: { kind: M, detail: '(code: number): BooExpectChain', doc: 'Assert HTTP status equals `code`.', snippet: 'status(${1:code})', next: 'BooExpectChain' },
    ok: { kind: P, detail: 'BooExpectChain', doc: 'Assert the value is truthy.', next: 'BooExpectChain' },
    exist: { kind: P, detail: 'BooExpectChain', doc: 'Assert the value is not null or undefined.', next: 'BooExpectChain' },
    not: { kind: P, detail: 'BooExpectNotChain', doc: 'Negate the following assertion.', next: 'BooExpectNotChain' },
    to: { kind: P, detail: 'BooExpectChain', next: 'BooExpectChain' },
    be: { kind: P, detail: 'BooExpectChain', next: 'BooExpectChain' },
    have: { kind: P, detail: 'BooExpectChain', next: 'BooExpectChain' },
  },
  // The negated chain implements a SMALLER set than the positive one — keep this in sync
  // with buildExpectNot() in scriptSandbox.js so we never suggest a method that throws.
  BooExpectNotChain: {
    equal: { kind: M, detail: '(expected: any): BooExpectNotChain', doc: 'Assert the value does NOT strictly equal `expected`.', snippet: 'equal(${1:expected})', next: 'BooExpectNotChain' },
    include: { kind: M, detail: '(item: any): BooExpectNotChain', doc: 'Assert the string/array does NOT include `item`.', snippet: 'include(${1:item})', next: 'BooExpectNotChain' },
    status: { kind: M, detail: '(code: number): BooExpectNotChain', doc: 'Assert HTTP status does NOT equal `code`.', snippet: 'status(${1:code})', next: 'BooExpectNotChain' },
    ok: { kind: P, detail: 'BooExpectNotChain', doc: 'Assert the value is falsy.', next: 'BooExpectNotChain' },
    exist: { kind: P, detail: 'BooExpectNotChain', doc: 'Assert the value is null or undefined.', next: 'BooExpectNotChain' },
    to: { kind: P, detail: 'BooExpectNotChain', next: 'BooExpectNotChain' },
    be: { kind: P, detail: 'BooExpectNotChain', next: 'BooExpectNotChain' },
    have: { kind: P, detail: 'BooExpectNotChain', next: 'BooExpectNotChain' },
  },
};

// Curated, high-value snippets (the patterns people actually write). Surfaced only at the
// start of a statement so they never clutter mid-expression completion. Mirrors Postman's
// "Snippets" — kept deliberately small; every one uses only the real runtime API.
const SNIPPETS = [
  {
    label: 'boo.test — status code is 200',
    doc: 'Assert the response status is 200.',
    keywords: 'test status code 200 ok',
    body: 'boo.test("Status code is 200", () => {\n\tboo.response.to.have.status(${1:200});\n});',
  },
  {
    label: 'boo.test — body value equals',
    doc: 'Assert a field in the JSON response body equals a value.',
    keywords: 'test body json expect equal property',
    body: 'boo.test("${1:Body has expected value}", () => {\n\tboo.expect(boo.response.json().${2:key}).to.equal(${3:value});\n});',
  },
  {
    label: 'boo.test — response time is fast',
    doc: 'Assert the response time is below a threshold.',
    keywords: 'test response time performance below ms',
    body: 'boo.test("Response time is acceptable", () => {\n\tboo.expect(boo.response.responseTime).to.below(${1:500});\n});',
  },
  {
    label: 'boo.test — has header',
    doc: 'Assert the response includes a header.',
    keywords: 'test header have content-type',
    body: 'boo.test("Has ${1:Content-Type} header", () => {\n\tboo.response.to.have.header("${1:Content-Type}");\n});',
  },
  {
    label: 'boo.environment.set — save token',
    doc: 'Write a value to the active environment (persists across requests).',
    keywords: 'environment set variable token save',
    body: 'boo.environment.set("${1:key}", ${2:value});',
  },
  {
    label: 'boo.variables.set — from response',
    doc: 'Save a value from the response into a request-scoped variable.',
    keywords: 'variables set local response json save',
    body: 'boo.variables.set("${1:name}", boo.response.json().${2:key});',
  },
];

const ROOT_GLOBALS = {
  boo: 'The `boo` object: read the request/response, write test assertions, and get/set variables.',
  pm: 'Alias for `boo` (Postman-compatible).',
};

// Strip balanced `(...)` call args (innermost-out) so an access chain can be split on `.`
// without args like `expect(a.b)` corrupting the segments.
function stripCallArgs(chain) {
  let prev;
  let out = chain;
  do {
    prev = out;
    out = out.replace(/\([^()]*\)/g, '');
  } while (out !== prev);
  return out;
}

// Resolve the type at the end of an access chain like `boo.response.to`.
// Returns the type's member map, or null if the chain doesn't resolve.
function resolveMembers(chain) {
  const segs = stripCallArgs(chain)
    .split('.')
    .map((s) => s.trim())
    .filter(Boolean);
  if (segs.length === 0) return null;
  const root = segs[0];
  if (root !== 'boo' && root !== 'pm') return null;
  let type = 'Boo';
  for (let i = 1; i < segs.length; i += 1) {
    const member = TYPES[type] && TYPES[type][segs[i]];
    if (!member || !member.next) return null;
    type = member.next;
  }
  return TYPES[type] || null;
}

export function registerBooScriptCompletions(monaco) {
  if (typeof window !== 'undefined') {
    if (window.__APIBOO_BOO_COMPLETIONS__) return;
    window.__APIBOO_BOO_COMPLETIONS__ = true;
  }

  const kindOf = (kind) =>
    kind === 'method'
      ? monaco.languages.CompletionItemKind.Method
      : monaco.languages.CompletionItemKind.Property;

  const provider = {
    triggerCharacters: ['.'],
    provideCompletionItems(model, position) {
      const lineText = model.getLineContent(position.lineNumber);
      const before = lineText.substring(0, position.column - 1);
      const word = model.getWordUntilPosition(position);
      const range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      };

      // Member access: `<chain>.<partial>` where chain is rooted at boo/pm.
      const memberMatch = before.match(
        /(?:^|[^\w$.])((?:boo|pm)(?:\s*\.\s*[\w$]+(?:\s*\([^()]*\))?)*)\s*\.\s*[\w$]*$/
      );
      if (memberMatch) {
        const members = resolveMembers(memberMatch[1]);
        if (!members) return { suggestions: [] };
        return {
          suggestions: Object.entries(members).map(([name, def]) => ({
            label: name,
            kind: kindOf(def.kind),
            detail: def.detail,
            documentation: def.doc ? { value: def.doc } : undefined,
            insertText: def.snippet || name,
            insertTextRules: def.snippet
              ? monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet
              : undefined,
            range,
          })),
        };
      }

      // Bare identifier (not a member access): suggest the `boo` / `pm` globals.
      const charBefore = before.slice(-(word.word.length + 1), before.length - word.word.length);
      if (charBefore === '.') return { suggestions: [] };

      const suggestions = Object.entries(ROOT_GLOBALS).map(([name, doc]) => ({
        label: name,
        kind: monaco.languages.CompletionItemKind.Variable,
        detail: 'Apiboo script API',
        documentation: { value: doc },
        insertText: name,
        sortText: `0_${name}`,
        range,
      }));

      // Full-statement snippets only at the start of a statement (line is blank up to the
      // word being typed) — never mid-expression like `const x = bo|`.
      const lineBeforeWord = before.slice(0, before.length - word.word.length);
      if (/^\s*$/.test(lineBeforeWord)) {
        for (const s of SNIPPETS) {
          suggestions.push({
            label: s.label,
            kind: monaco.languages.CompletionItemKind.Snippet,
            detail: 'snippet',
            documentation: { value: `${s.doc}\n\n\`\`\`js\n${s.body.replace(/\$\{\d+:?([^}]*)\}/g, '$1').replace(/\$0/g, '')}\n\`\`\`` },
            insertText: s.body,
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            filterText: `${s.label} ${s.keywords}`,
            sortText: `1_${s.label}`,
            range,
          });
        }
      }

      return { suggestions };
    },
  };

  // The script editors run in the `javascript` language mode.
  return monaco.languages.registerCompletionItemProvider('javascript', provider);
}
